"use client";

import {
  startTransition,
  useActionState,
  useEffect,
  useRef,
  useState,
} from "react";
import { CheckCircle2 } from "lucide-react";
import { submitInquiry, type InquiryState } from "@/lib/actions/inquiry";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { Button } from "@/components/ui/Button";
import { Input, Label, Textarea, DatePicker } from "@/components/ui/Fields";
import { cn } from "@/lib/format";

type GtagFn = (...args: unknown[]) => void;

function fireGtag(event: string, params: Record<string, string | number>) {
  if (typeof window !== "undefined") {
    const w = window as unknown as { gtag?: GtagFn };
    w.gtag?.("event", event, params);
  }
}

/** Optional fields that can be individually shown/hidden. Name, email, and
 *  message are always present — the backend requires them. */
export type InquiryFieldKey = "phone" | "travelers" | "subject" | "travelDate";

type Props = {
  tripTitle?: string;
  tripSlug?: string;
  defaultSubject?: string;
  /** Pre-fills the message textarea, e.g. with a specific departure date. */
  defaultMessage?: string;
  /** @deprecated Use `fields={{ travelDate: true }}` instead. Still honored
   *  when `fields` is not passed. */
  enableDate?: boolean;
  /** Stack every field in a single column instead of the 2-up grid. */
  vertical?: boolean;
  /** Show each field's `<Label>`. Defaults to true; set false for a
   *  compact, placeholder-only layout. */
  showLabels?: boolean;
  /** Which optional fields to render. Omitted keys keep their default
   *  (phone/travelers/subject on, travelDate off unless `enableDate` is
   *  set). Pass `false` to hide a field that's on by default. */
  fields?: Partial<Record<InquiryFieldKey, boolean>>;
  className?: string;
};

export function InquiryForm({
  tripTitle,
  tripSlug,
  defaultSubject,
  defaultMessage,
  enableDate,
  vertical = false,
  showLabels = true,
  fields,
  className,
}: Props) {
  const defaultVisibility: Record<InquiryFieldKey, boolean> = {
    phone: true,
    travelers: true,
    subject: true,
    travelDate: Boolean(enableDate),
  };
  const isVisible = (key: InquiryFieldKey) =>
    fields?.[key] ?? defaultVisibility[key];
  const [ts] = useState(() => String(Date.now()));
  // Lazy-initialized so it reads window.location on the client's first render
  // without a separate effect (SSR pass yields "").
  const [pageUrl] = useState(() =>
    typeof window !== "undefined" ? window.location.href : "",
  );
  const [awaitingCaptcha, setAwaitingCaptcha] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [state, action, pending] = useActionState<
    InquiryState | null,
    FormData
  >(submitInquiry, null);

  // Fetch a reCAPTCHA v3 token (no-op when reCAPTCHA isn't configured), attach
  // it to the submitted FormData, and dispatch the action directly — a single
  // path with no re-entrant native `requestSubmit()` round-trip, so there's no
  // window where a first click can be silently swallowed by the browser's own
  // constraint validation re-running. `pending` only flips true once dispatch
  // happens, which leaves a gap during the token fetch with no loading
  // feedback — `awaitingCaptcha` covers that gap.
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!e.currentTarget.reportValidity()) return;
    setAwaitingCaptcha(true);
    const formData = new FormData(e.currentTarget);
    getRecaptchaToken("inquiry").then((token) => {
      formData.set("captchaToken", token);
      setAwaitingCaptcha(false);
      startTransition(() => action(formData));
    });
  };

  useEffect(() => {
    if (state?.ok) {
      fireGtag("generate_lead", {
        event_category: "inquiry",
        event_label: tripTitle || tripSlug || "general",
      });
      // Clear the (uncontrolled) fields so the visible form reads as fresh
      // once the success banner appears above it, rather than still showing
      // what was just submitted.
      formRef.current?.reset();
    }
  }, [state?.ok, tripTitle, tripSlug]);

  const err =
    state && !state.ok && state.code === "validation"
      ? (state.fields ?? {})
      : {};

  return (
    <form
      ref={formRef}
      action={action}
      onSubmit={handleSubmit}
      className={cn("space-y-4", className)}
    >
      {state?.ok ? (
        <div
          role="status"
          className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4"
        >
          <CheckCircle2
            className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
            aria-hidden
          />
          <div>
            <p className="text-sm text-emerald-800">{state.message}</p>
          </div>
        </div>
      ) : null}

      <input type="hidden" name="ts" value={ts} />
      <input type="hidden" name="trip" value={tripTitle || tripSlug || ""} />
      <input type="hidden" name="url" value={pageUrl} />
      <input type="hidden" name="captchaToken" defaultValue="" />
      <input
        type="hidden"
        name="type"
        value={tripTitle || tripSlug ? "Trip inquiry" : "Contact us"}
      />
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute left-[-9999px]"
      />

      <div
        className={cn(
          "grid gap-4",
          vertical ? "grid-cols-1" : "sm:grid-cols-2",
        )}
      >
        <Field
          label="Your name"
          name="name"
          error={err.name}
          autoComplete="name"
          required
          showLabel={showLabels}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          error={err.email}
          autoComplete="email"
          required
          showLabel={showLabels}
        />
        {isVisible("phone") ? (
          <Field
            label="Phone (optional)"
            name="phone"
            type="tel"
            error={err.phone}
            autoComplete="tel"
            showLabel={showLabels}
          />
        ) : null}
        {isVisible("travelers") ? (
          <Field
            label="Number of travelers (optional)"
            name="travelers"
            type="number"
            error={err.travelers}
            showLabel={showLabels}
          />
        ) : null}

        {isVisible("travelDate") ? (
          <div className={vertical ? undefined : "sm:col-span-2"}>
            {showLabels ? (
              <Label htmlFor="travelDate">
                Expected Travel Date (optional)
              </Label>
            ) : null}
            <DatePicker
              id="travelDate"
              name="travelDate"
              error={Boolean(err.travelDate)}
              aria-label={showLabels ? undefined : "Expected travel date"}
            />
          </div>
        ) : null}
      </div>

      {isVisible("subject") ? (
        <Field
          label="Subject"
          name="subject"
          defaultValue={
            defaultSubject || (tripTitle ? `Inquiry: ${tripTitle}` : "")
          }
          error={err.subject}
          showLabel={showLabels}
        />
      ) : null}
      <div>
        {showLabels ? <Label htmlFor="message">Your message</Label> : null}
        <Textarea
          id="message"
          name="message"
          rows={5}
          required
          defaultValue={defaultMessage}
          error={Boolean(err.message)}
          placeholder={
            tripTitle
              ? `Tell us about your plans for ${tripTitle}…`
              : "Group size, special requests…"
          }
          aria-label={showLabels ? undefined : "Your message"}
          aria-invalid={Boolean(err.message)}
          aria-describedby={err.message ? "message-err" : undefined}
        />
        {err.message ? (
          <p id="message-err" className="mt-1 text-xs text-red-600">
            {err.message}
          </p>
        ) : null}
      </div>

      {state && !state.ok ? (
        <p className="text-sm text-red-600" role="alert">
          {state.message}
        </p>
      ) : null}

      <Button
        type="submit"
        variant="primary"
        disabled={pending || awaitingCaptcha}
        size="md"
        className="w-full tracking-wider sm:w-auto"
      >
        {pending || awaitingCaptcha ? "Sending…" : "SUBMIT"}
      </Button>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  defaultValue,
  autoComplete,
  error,
  showLabel = true,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  defaultValue?: string;
  autoComplete?: string;
  error?: string;
  showLabel?: boolean;
}) {
  const id = `f-${name}`;
  return (
    <div>
      {showLabel ? <Label htmlFor={id}>{label}</Label> : null}
      <Input
        id={id}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        error={Boolean(error)}
        placeholder={
          showLabel ? undefined : label.replace(/\s*\(optional\)$/, "")
        }
        aria-label={showLabel ? undefined : label}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : undefined}
      />
      {error ? (
        <p id={`${id}-err`} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
